/**
 * 관리자 Web Push (Service Worker) 클라이언트.
 *
 * 백엔드 연동:
 *   GET    /api/v1/admin/push/vapid-public-key   공개키
 *   POST   /api/v1/admin/push/subscribe           { endpoint, p256dh, auth }
 *   DELETE /api/v1/admin/push/unsubscribe         { endpoint }
 *   POST   /api/v1/admin/push/test                테스트 발송
 *
 * 흐름:
 *   1) registerServiceWorker()   — 앱 부팅 시 1회
 *   2) ensurePushSubscription() — 권한 있을 때만 자동 구독, 없으면 no-op
 *   3) enablePush()             — 사용자 클릭으로 권한 요청 → 구독
 *   4) disablePush()            — 구독 해제
 *   5) sendTestPush()           — 본인에게 테스트 알림
 */

import { apiService } from './api'

const SW_PATH = '/sw.js'

let cachedRegistration: ServiceWorkerRegistration | null = null

/** Service Worker 지원 여부 */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** 현재 알림 권한 상태 ('default' | 'granted' | 'denied' | 'unsupported') */
export function getPermissionState(): NotificationPermission | 'unsupported' {
  if (!isPushSupported()) return 'unsupported'
  return Notification.permission
}

/** Service Worker 등록 (앱 부팅 시 1회) */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) {
    console.warn('[push] Service Worker / PushManager / Notification API 미지원')
    return null
  }
  try {
    if (cachedRegistration) return cachedRegistration
    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' })
    await navigator.serviceWorker.ready
    cachedRegistration = reg
    console.log('[push] Service Worker 등록 완료', reg.scope)
    return reg
  } catch (err) {
    console.error('[push] Service Worker 등록 실패:', err)
    return null
  }
}

/** base64url -> Uint8Array (applicationServerKey 용). ArrayBuffer 명시로 TS 호환. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const buf = new ArrayBuffer(raw.length)
  const out = new Uint8Array(buf)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/** PushSubscription 의 p256dh / auth 키를 base64url 문자열로 추출 */
function extractKey(sub: PushSubscription, name: 'p256dh' | 'auth'): string {
  const buf = sub.getKey(name)
  if (!buf) return ''
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return window
    .btoa(bin)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

interface VapidKeyResponse {
  result?: string
  data?: { public_key: string }
  public_key?: string
}

async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const resp = await apiService.get<VapidKeyResponse>('/api/v1/admin/push/vapid-public-key')
    const pub = (resp as any)?.public_key || (resp as any)?.data?.public_key
    return pub || null
  } catch (err) {
    console.error('[push] VAPID 공개키 조회 실패:', err)
    return null
  }
}

/** 현재 구독 상태 조회 (등록된 SW 기준) */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const reg = await registerServiceWorker()
  if (!reg) return null
  return reg.pushManager.getSubscription()
}

/**
 * 권한 있으면 구독, 없으면 no-op (자동 호출 시 사용).
 * 권한 요청을 강제하지 않음 (브라우저 정책 + UX).
 */
export async function ensurePushSubscription(): Promise<{ ok: boolean; reason?: string }> {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }
  if (Notification.permission !== 'granted') {
    return { ok: false, reason: 'no-permission' }
  }
  return _subscribeAndRegister()
}

/**
 * 사용자 클릭으로 호출. 권한 요청 → 구독 → 백엔드 등록.
 * 권한 거부 시 false 리턴.
 */
export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }

  if (Notification.permission === 'denied') {
    return { ok: false, reason: 'denied' }
  }
  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission()
    if (result !== 'granted') return { ok: false, reason: 'denied' }
  }

  return _subscribeAndRegister()
}

/** SW 등록 후 push 구독 + 백엔드 전송 */
async function _subscribeAndRegister(): Promise<{ ok: boolean; reason?: string }> {
  try {
    const reg = await registerServiceWorker()
    if (!reg) return { ok: false, reason: 'sw-failed' }

    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      const publicKey = await fetchVapidPublicKey()
      if (!publicKey) return { ok: false, reason: 'no-vapid-key' }
      try {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        })
      } catch (err) {
        console.error('[push] PushManager.subscribe 실패:', err)
        return { ok: false, reason: 'subscribe-failed' }
      }
    }

    const payload = {
      endpoint: sub.endpoint,
      p256dh: extractKey(sub, 'p256dh'),
      auth: extractKey(sub, 'auth'),
    }
    if (!payload.p256dh || !payload.auth) {
      return { ok: false, reason: 'invalid-keys' }
    }

    await apiService.post('/api/v1/admin/push/subscribe', payload)
    console.log('[push] 구독 등록 완료:', sub.endpoint.slice(0, 60) + '...')
    return { ok: true }
  } catch (err) {
    console.error('[push] _subscribeAndRegister 오류:', err)
    return { ok: false, reason: 'unknown' }
  }
}

/** 현재 구독 해제 + 백엔드 비활성화 */
export async function disablePush(): Promise<boolean> {
  try {
    const sub = await getCurrentSubscription()
    if (!sub) return true
    const endpoint = sub.endpoint
    try {
      await apiService.delete('/api/v1/admin/push/unsubscribe', { data: { endpoint } } as any)
    } catch (err) {
      console.warn('[push] 백엔드 unsubscribe 실패 (무시):', err)
    }
    try {
      await sub.unsubscribe()
    } catch (err) {
      console.warn('[push] 브라우저 unsubscribe 실패:', err)
    }
    return true
  } catch (err) {
    console.error('[push] disablePush 오류:', err)
    return false
  }
}

/** 본인에게 테스트 알림 발송 (디버그) */
export async function sendTestPush(): Promise<{ sent: number; failed: number; removed: number } | null> {
  try {
    const resp: any = await apiService.post('/api/v1/admin/push/test', {})
    return {
      sent: resp?.sent ?? 0,
      failed: resp?.failed ?? 0,
      removed: resp?.removed ?? 0,
    }
  } catch (err) {
    console.error('[push] sendTestPush 오류:', err)
    return null
  }
}

/** SW 가 보낸 navigate 메시지를 받아 react-router 로 이동 */
export function setupSwNavigationListener(navigate: (url: string) => void) {
  if (!('serviceWorker' in navigator)) return
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data
    if (data && data.type === 'sw:navigate' && typeof data.url === 'string') {
      try {
        const url = data.url.startsWith('http')
          ? new URL(data.url).pathname + new URL(data.url).search
          : data.url
        navigate(url)
      } catch (e) {
        console.warn('[push] navigate 실패:', e)
      }
    }
  })
}
